import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { celebrateWin } from "@/lib/wins";
import { getAudioCtx } from "@/lib/audio";
import { requestAvatarMessage, deliverWeeklySummary } from "@/lib/api";
import { refreshWeatherIfPermitted } from "@/lib/weatherClient";

// One-shot app startup orchestration:
//   1. Load all data
//   2. If user was away ≥1 day, play a "Mochi waited for you" speech bubble
//   3. If today's morning check-in hasn't fired yet, open the MorningCheckIn
//   4. Sunday afternoon → silently deliver a weekly recap into chat
//   5. Ask once for browser notification permission
// Also wires a first-gesture (`pointerdown` / `keydown`) listener to unlock
// Web Audio and re-prompt for notifications.
//
//   useMorningBootstrap({
//       today, loadAll, idleMessage,
//       setBubbleMsg, setBubbleLoading,
//       setMorningGreeting, setMorningOpen,
//   });
export function useMorningBootstrap({
    today,
    loadAll,
    idleMessage,
    setBubbleMsg,
    setBubbleLoading,
    setMorningGreeting,
    setMorningOpen,
    onReturnFromAbsence,
}) {
    const audioUnlockedRef = useRef(false);

    // Initial bootstrap (mount-only)
    useEffect(() => {
        (async () => {
            const { settings: s, stats: st } = await loadAll();

            const daysAway = st?.days_away_on_return || 0;
            if (daysAway >= 1) {
                // Fire the "Mochi waited for you" intro animation overlay first
                // (parent unmounts it after ~2.4s). The speech bubble lands
                // right after so they don't compete.
                onReturnFromAbsence?.(daysAway);
                celebrateWin("return");
                setBubbleLoading(true);
                try {
                    const ev = daysAway >= 3 ? "miss_you_3" : daysAway === 2 ? "miss_you_2" : "miss_you_1";
                    const r = await requestAvatarMessage(ev, "cozy", { absence_days: daysAway });
                    setBubbleMsg(r.message);
                } catch (e) {
                    console.warn("absence greeting AI failed — using curated line", e);
                    setBubbleMsg(daysAway >= 3
                        ? "I'm not mad. I'm just… reorganizing again."
                        : daysAway === 2
                        ? "I reorganized your tasks while you were gone."
                        : "I saved your spot 🥺");
                }
                setBubbleLoading(false);
                await new Promise((r) => setTimeout(r, 2400));
            }

            if (s.morning_checkin_date !== today) {
                setBubbleLoading(true);
                try {
                    const r = await requestAvatarMessage("morning_greeting", "excited");
                    setMorningGreeting(r.message);
                    setBubbleMsg(r.message);
                } catch (e) {
                    console.warn("morning greeting AI failed — using curated line", e);
                    setMorningGreeting("Good morning! Let's set up a soft, lovely day. 🌸");
                    setBubbleMsg("Good morning! 🌸");
                }
                setBubbleLoading(false);
                setMorningOpen(true);
            } else {
                idleMessage();
            }

            // Sunday afternoon weekly recap (gifted, not pushy)
            try {
                const nowLocal = new Date();
                const isSunday = nowLocal.getDay() === 0;
                const afterFour = nowLocal.getHours() >= 16;
                const last = window.localStorage.getItem("mochi-weekly-recap");
                if (isSunday && afterFour && last !== today) {
                    await deliverWeeklySummary("default");
                    window.localStorage.setItem("mochi-weekly-recap", today);
                    toast("📜 Mochi tucked your weekly recap into the chat.", { duration: 5000 });
                }
            } catch (e) { console.warn("weekly recap delivery failed (will retry next Sunday)", e); }

            if ("Notification" in window && Notification.permission === "default") {
                // Notification.requestPermission can throw in iframes / older
                // Safari — silent because there's nothing actionable here.
                try { Notification.requestPermission(); } catch { /* notif API unsupported in this context */ }
            }

            // Background-refresh weather if the user previously opted in.
            // Silent; never re-prompts. Snapshot is read by chat / idle bubbles.
            try { await refreshWeatherIfPermitted(); } catch (e) { console.warn("weather refresh failed (non-critical)", e); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // First-gesture unlock: enables audio + re-prompts notifications
    useEffect(() => {
        const unlock = () => {
            if (audioUnlockedRef.current) return;
            audioUnlockedRef.current = true;
            // AudioContext construction can throw if the page is sandboxed
            // without "autoplay" allowed — fine, audio simply stays muted.
            try { getAudioCtx(); } catch { /* audio context unsupported in this context */ }
            if ("Notification" in window && Notification.permission === "default") {
                try { Notification.requestPermission(); } catch { /* notif API unsupported in this context */ }
            }
        };
        window.addEventListener("pointerdown", unlock, { once: true });
        window.addEventListener("keydown", unlock, { once: true });
        return () => {
            window.removeEventListener("pointerdown", unlock);
            window.removeEventListener("keydown", unlock);
        };
    }, []);
}
